/** Shared building blocks. Nothing clever — just the shapes this workspace repeats. */

import { Moon, Sun } from 'lucide-react'
import { classify } from '../lib/logLines'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { useT } from '../i18n'

export function Card({
  title,
  sub,
  actions,
  children,
  className,
  color,
}: {
  title?: ReactNode
  sub?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  /** a direction's colour: the card stands on it */
  color?: string
}) {
  return (
    <section className={`card${className ? ` ${className}` : ''}`} data-color={color}>
      {(title !== undefined || actions) && (
        <header>
          {title !== undefined && <h2>{title}</h2>}
          {sub ? <span className="sub">{sub}</span> : null}
          <span className="grow" />
          {actions}
        </header>
      )}
      {children}
    </section>
  )
}

export function Badge({
  kind,
  title,
  children,
}: {
  kind?: 'ok' | 'bad' | 'warn'
  /** the long version, for a badge that has to stay short — e.g. every SDK package with an update */
  title?: string
  children: ReactNode
}) {
  return (
    <span className={`badge${kind ? ` ${kind}` : ''}`} title={title}>
      {children}
    </span>
  )
}

export function Pill({ mono, title, children }: { mono?: boolean; title?: string; children: ReactNode }) {
  return (
    <span className={`pill${mono ? ' mono' : ''}`} title={title}>
      {children}
    </span>
  )
}

export function Dot({ status }: { status: 'online' | 'offline' | 'busy' }) {
  return <span className={`dot ${status}`} />
}

export function Status({ text, bad }: { text?: string | null; bad?: boolean }) {
  if (!text) return null
  return <span className={`tiny ${bad ? 'badtext' : 'dim'}`} style={bad ? { color: 'var(--bad)' } : undefined}>{text}</span>
}

/** Log view that follows the tail unless the reader has scrolled up to look at something. */
export function LogView({ text, height }: { text: string; height?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)

  useEffect(() => {
    const el = ref.current
    if (el && pinned.current) el.scrollTop = el.scrollHeight
  }, [text])

  return (
    <div
      className="log"
      ref={ref}
      style={height ? { height } : undefined}
      onScroll={(e) => {
        const el = e.currentTarget
        pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24
      }}
    >
      {/* Coloured per line rather than shown as one block of text: a build prints a few hundred lines and
          three of them matter, and those three are exactly what someone is looking for when they open it.
          The classifier lives in `lib/logLines` so its rules can be tested — the ordering is the design
          (a failure must win over a line that also contains the word "build"). */}
      {text.split('\n').map((line, i) => (
        <div key={i} className={`log-line log-${classify(line)}`}>
          {line || '\u00a0'}
        </div>
      ))}
    </div>
  )
}

export function Chips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ id: T; label: ReactNode }>
  value: T
  onChange: (id: T) => void
}) {
  return (
    <div className="chips">
      {options.map((o) => (
        <button
          key={o.id}
          className={`chip${o.id === value ? ' on' : ''}`}
          aria-pressed={o.id === value}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

export function Skeletons({ count = 3 }: { count?: number }) {
  return (
    <div className="devices" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <div className="skeleton" key={i} />
      ))}
    </div>
  )
}

/** Dark by default — testers watch a phone screen, and a bright page fights the video. */
export function ThemeToggle() {
  const t = useT()
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('tthub_theme') as 'dark' | 'light') ?? 'dark',
  )

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    localStorage.setItem('tthub_theme', theme)
  }, [theme])

  return (
    <button
      className="ghost icon"
      title={theme === 'dark' ? t.nav.lightTheme : t.nav.darkTheme}
      aria-label={theme === 'dark' ? t.nav.lightTheme : t.nav.darkTheme}
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
    >
      {theme === 'dark' ? <Sun className="ico" size={16} aria-hidden /> : <Moon className="ico" size={16} aria-hidden />}
    </button>
  )
}
